import { Box, Button, Card, Center, Loader, Text } from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate, useSearchParams } from 'react-router'
import { deploymentApi } from '@/api/deploymentApi'
import { deviceApi } from '@/api/deviceApi'
import { modelApi } from '@/api/modelApi'
import { PageWithFixedToolbar } from '@/components/layout/PageWithFixedToolbar/PageWithFixedToolbar'
import { useApplicationContext } from '@/providers/ApplicationContext'
import { RouterPath } from '@/router/paths'
import {
  CameraSettingsPayload,
  Deployment,
  InputSourceType
} from '@/types/deployment'
import { Device } from '@/types/device'
import { MLModel } from '@/types/model'
import { getAugmentedCameras } from '@/utils/device/getAugmentedCameras'
import { MAX_ITEMS_WITHOUT_PAGINATION } from '@/utils/pagination'
import { ErrorWithReload } from '../../ui-shared/ErrorWithReload/ErrorWithReload'
import { DeploymentEditorToolbar } from './DeploymentEditorToolbar/DeploymentEditorToolbar'
import { DeploymentHandler } from './DeploymentHandler'
import { buildSavedStreamLogic } from './helpers/buildSavedStreamLogic'
import { getInitialEventEndpoints } from './helpers/getInitialEventEndpoints'
import { getInitialGpuSelection, getRunningGpus } from './helpers/gpuSelection'

const STEP_QUERY_PARAM = 'step'

type DeploymentWithSettings = Omit<Deployment, 'settings_payload'> & {
  settings_payload: NonNullable<Deployment['settings_payload']>
}

async function getInitialDeploymentData(applicationId: string): Promise<{
  deployments: DeploymentWithSettings[]
  hasExistingDeployment: boolean
  devices: Device[]
  primaryModel: MLModel | null
  secondaryModel: MLModel | null
}> {
  let primaryModel: MLModel | null = null
  let secondaryModel: MLModel | null = null

  const { results: allDeployments } = await deploymentApi.getDeploymentList({
    application_id: applicationId,
    limit: MAX_ITEMS_WITHOUT_PAGINATION
  })

  const deployments = allDeployments.filter(
    (d): d is DeploymentWithSettings => d.settings_payload !== null
  )

  const uniqueDeviceIds = [
    ...new Set(deployments.map((d) => d.settings_payload.device_id))
  ]

  const devices = await Promise.all(
    uniqueDeviceIds.map((id) => deviceApi.getDeviceDetails(id))
  )

  if (deployments.length > 0) {
    const modelPromises = [
      modelApi.getModelDetails(deployments[0].primary_ml_model.id)
    ]

    if (deployments[0].secondary_ml_model) {
      modelPromises.push(
        modelApi.getModelDetails(deployments[0].secondary_ml_model.id)
      )
    }

    const [primaryModelResult, secondaryModelResult] =
      await Promise.all(modelPromises)

    primaryModel = primaryModelResult
    secondaryModel = secondaryModelResult || null
  }

  return {
    deployments,
    hasExistingDeployment: allDeployments.length > 0,
    devices,
    primaryModel,
    secondaryModel
  }
}

export const DeploymentEditorPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { application } = useApplicationContext()

  // Use a mutation to get fresh initial deployment data; prevents initializing the deployment editor with data from the stale query cache
  const { mutate, data, isPending, isError, isSuccess } = useMutation({
    mutationFn: getInitialDeploymentData
  })

  const {
    deployments,
    hasExistingDeployment,
    devices,
    primaryModel,
    secondaryModel
  } = data || {
    deployments: [],
    hasExistingDeployment: false,
    devices: [],
    primaryModel: null,
    secondaryModel: null
  }

  const stepParamValue = searchParams.get(STEP_QUERY_PARAM)
  const initialStep = stepParamValue ? parseInt(stepParamValue) : 0

  const initialDeviceIds = useMemo(() => devices.map((d) => d.id), [devices])

  const cameraStreamIds = useMemo(
    () =>
      deployments
        .filter(
          (d) =>
            d.settings_payload.input_source_type === InputSourceType.Cameras
        )
        .map((d) => (d.settings_payload as CameraSettingsPayload).stream_id),
    [deployments]
  )

  const initialCameraStreams = useMemo(
    () =>
      devices
        .map(getAugmentedCameras)
        .flat()
        .filter((stream) => cameraStreamIds.includes(stream.camera_id)),
    [devices, cameraStreamIds]
  )

  const initialStreamLogics = useMemo(
    () => buildSavedStreamLogic(deployments, initialCameraStreams),
    [deployments, initialCameraStreams]
  )

  const initialEventEndpoints = useMemo(
    () => getInitialEventEndpoints(deployments),
    [deployments]
  )

  const deploymentSettings = deployments[0]?.settings_payload

  const inputSourceInitialSettings = deploymentSettings
    ? deploymentSettings.input_source_type === InputSourceType.Cameras
      ? {
          initialCameraStreams,
          initialStreamLogics,
          initialEventEndpoints,
          initialEventVideoDurationTime:
            deploymentSettings.event_video_duration_time,
          initialPreEventSeconds:
            deployments[0]?.deployment_settings?.pre_event_s,
          initialInferenceInterval:
            deployments[0]?.deployment_settings?.interval,
          initialCacheSeconds: deployments[0]?.deployment_settings?.cache_s,
          initialMinDetectionFrames: deploymentSettings.min_detection_frames,
          initialNotificationSettings: deployments[0]?.notification_settings,
          initialOcrSettings: deployments[0]?.ocr_settings
        }
      : {
          initialMediaFolderConfig: {
            input_folder_path: deploymentSettings.input_folder_path,
            output_folder_path: deploymentSettings.output_folder_path,
            delete_processed: deploymentSettings.delete_processed
          }
        }
    : {}

  const isEditMode = deployments.length > 0
  const isBrokenDeployment = hasExistingDeployment && !isEditMode

  useEffect(() => {
    if (application?.id) {
      void mutate(application.id)
    }
  }, [application?.id, mutate])

  const handleReload = () => {
    if (application?.id) {
      void mutate(application.id)
    }
  }

  const handleCancelEdit = () => {
    void navigate(RouterPath.deployments)
  }

  return (
    <PageWithFixedToolbar
      toolbar={
        isSuccess && (
          <DeploymentEditorToolbar
            applicationName={application?.name}
            mode={isEditMode ? 'edit' : 'create'}
            onCancelEdit={handleCancelEdit}
          />
        )
      }
    >
      {isError && (
        <ErrorWithReload onReload={handleReload}>
          <FormattedMessage id="deployments.editor.initError" />
        </ErrorWithReload>
      )}

      {isPending && (
        <Center h={120}>
          <Loader />
        </Center>
      )}

      {isSuccess && isBrokenDeployment && (
        <Card mt="md" withBorder>
          <Center mih={240}>
            <Box ta="center">
              <Text size="sm" fw="bold" mb="xl">
                <FormattedMessage id="deployments.editor.brokenDeploymentError" />
              </Text>

              <Button
                size="xs"
                color="brand-secondary"
                onClick={handleCancelEdit}
              >
                <FormattedMessage id="general.backToList" />
              </Button>
            </Box>
          </Center>
        </Card>
      )}

      {isSuccess && !isBrokenDeployment && (
        <Box pt="md">
          <DeploymentHandler
            mode={isEditMode ? 'edit' : 'create'}
            {...(isEditMode && {
              initialStep,
              initialPrimaryModel: primaryModel || undefined,
              initialSecondaryModel: secondaryModel || undefined,
              initialDeviceIds,
              initialConfidenceThresholds:
                deploymentSettings.confidence_threshold,
              initialBlurOptions: deploymentSettings.blur_options,
              initialInputSource: deploymentSettings.input_source_type,
              initialGpuIndex: deployments[0]?.gpu_index,
              initialRunInGroups:
                deployments[0]?.deployment_settings?.run_in_groups,
              initialGpuSelection: getInitialGpuSelection(deployments),
              initialRunningGpus: getRunningGpus(deployments),
              ...inputSourceInitialSettings
            })}
          />
        </Box>
      )}
    </PageWithFixedToolbar>
  )
}
